import { Injectable } from '@angular/core';
import { Observable, Subject, BehaviorSubject } from 'rxjs';

import { AuthenticatedHttpService } from './authenticatedhttp.service';

import { MasterOptionsModel } from '../model/master-options-model';
import { Option } from '../model/option';
import { SelectedOption } from '../model/selected-option';
import { OptionIncludeExclude } from '../model/option-include-exclude';
import { Adjustment } from '../model/adjustment';
import { PartCategory } from '../model/part-category';
import { Quote } from '../model/quote';

import { QuoteInfoService } from "./quote-info.service";
import { CurrentUserService } from "./current-user.service";

import * as _ from 'lodash';

@Injectable()
export class OptionsService {
    private baseUrl;

    _allNormalOptionsCache: Option[];
    _completeNormalOptionsList: Option[];
    _allSelectedOptionsCache: SelectedOption[];
    _allIncludeExcludeCache: OptionIncludeExclude[];
    //private _selectedOptionsCache: SelectedOption[];
	private _quote: Quote;

    callOptionsConfigSource = new Subject<any>();
    callOptionsConfig = this.callOptionsConfigSource.asObservable();
    checkSubOptions$: BehaviorSubject<boolean> = new BehaviorSubject(false);

    optionsChanged$: BehaviorSubject<boolean> = new BehaviorSubject(false);
    subOptionsChanged$: BehaviorSubject<boolean> = new BehaviorSubject(false);
    searchTerm$: BehaviorSubject<string> = new BehaviorSubject(null);
    noMatches: BehaviorSubject<Array<string>> = new BehaviorSubject([]);
    enableSaveButton$: BehaviorSubject<boolean> = new BehaviorSubject(true);
    resetSelectedOptions$: BehaviorSubject<boolean> = new BehaviorSubject(false);
    initialOptionPageLoad$: BehaviorSubject<boolean> = new BehaviorSubject(true);

    // added or changed for performance refactor
    allSelectedOptions$: BehaviorSubject<SelectedOption[]> = new BehaviorSubject([]);
    customOptionsfromDB$: BehaviorSubject<SelectedOption[]> = new BehaviorSubject([]);
    currentSelectedOptions$: BehaviorSubject<SelectedOption[]> = new BehaviorSubject([]);
    customOptions$: BehaviorSubject<SelectedOption[]> = new BehaviorSubject([]);
    normalOptions$: BehaviorSubject<Option[]> = new BehaviorSubject([]);
    allNormalOptions$: BehaviorSubject<Option[]> = new BehaviorSubject([]);
    selectedOptions$: BehaviorSubject<SelectedOption[]> = new BehaviorSubject([]);
    mappedOptions$: BehaviorSubject<SelectedOption[]> = new BehaviorSubject([]);
	includeExcludeAllSelectedOptions$: BehaviorSubject<SelectedOption[]> = new BehaviorSubject([]);
	AdminOptionsExist$: BehaviorSubject<boolean> = new BehaviorSubject(false);

    constructor(
        private _http: AuthenticatedHttpService,
        private quoteInfoService: QuoteInfoService,
        public currentUserService: CurrentUserService
    ) {
        this.baseUrl = _http.getBaseURLFromCookie();
    }

    getMasterOptions(quoteId: number, modelId: number, categoryId: number, currentPricingView: number): Observable<MasterOptionsModel> {

        return this._http.get(`${this.baseUrl}/api/master/${quoteId}/${modelId}/${categoryId}/${currentPricingView}`)
            .map(x => {
                //this._selectedOptionsCache = [];
                let masterOptions: MasterOptionsModel = x.json();

                if (masterOptions !== undefined && masterOptions !== null) {
                    this._allIncludeExcludeCache = _.cloneDeep(masterOptions.IncludeExcludeOptions);
                    this._allSelectedOptionsCache = _.cloneDeep(masterOptions.QuoteSelectedOptions);
                    this._allNormalOptionsCache = _.cloneDeep(masterOptions.NormalOptions);
                    this._completeNormalOptionsList = _.cloneDeep(masterOptions.AllNormalOptions);

                    if (masterOptions.QuoteSelectedOptions != undefined && masterOptions.QuoteSelectedOptions !== null && masterOptions.QuoteSelectedOptions.length > 0) {
                        if (categoryId !== 0) {
                            masterOptions.QuoteSelectedOptions = masterOptions.QuoteSelectedOptions.filter(x => x.HomeOptionsCategoryId === categoryId);
                        }
                        //this._selectedOptionsCache = masterOptions.QuoteSelectedOptions;
                    }
                }
                return masterOptions;
            })
            .catch(this._http.handleError);
    }

    markOptionChangedByFacility(option: SelectedOption, priceChanged = false) {
        if (priceChanged === true) {
            if (this.isPlantUser === true && this.quoteSubmitted === true) {
                return option.ChangedByFacility = true;
            }
        } else {
            if (this.isPlantUser === true && this.quoteSubmitted === true) {
                return option.ChangedByFacility = true;
            }
        }
    }

    markOptionNotChangedByFacility(option: SelectedOption) {
        return option.ChangedByFacility = false;
    }

    get quoteSubmitted(): boolean {
        let quoteStatus;
		this.quoteInfoService.quote$.subscribe(x => {
			this._quote = x;
            if (x.QuoteStatusesID === 1) {
                quoteStatus = true;
            } else {
                quoteStatus = false;
            }
        });
        return quoteStatus;
    }

    get isPlantUser(): boolean {
        let result;
        this.currentUserService.currentUser$.subscribe(x => {
            if (x.roles.includes('Plant_User')) {
                result = true
            } else {
                result = false;
            }
        });
        return result;
    }

    resetSelectedOptions(quoteId: number, categoryId: number, currentPricingView: number): Observable<MasterOptionsModel> {
        return this._http.get(`${this.baseUrl}/api/master/reset-options/${quoteId}/${categoryId}/${currentPricingView}`)
            .map(x => {
                //this._selectedOptionsCache = [];
                let masterOptions: MasterOptionsModel = x.json();

                if (masterOptions !== undefined && masterOptions !== null) {
                    this._allIncludeExcludeCache = _.cloneDeep(masterOptions.IncludeExcludeOptions);
                    this._allSelectedOptionsCache = _.cloneDeep(masterOptions.QuoteSelectedOptions);
                    this._allNormalOptionsCache = _.cloneDeep(masterOptions.NormalOptions);
                    this._completeNormalOptionsList = _.cloneDeep(masterOptions.AllNormalOptions);

                    if (masterOptions.QuoteSelectedOptions != undefined && masterOptions.QuoteSelectedOptions !== null && masterOptions.QuoteSelectedOptions.length > 0) {
                        if (categoryId !== 0) {
                            masterOptions.QuoteSelectedOptions = masterOptions.QuoteSelectedOptions.filter(x => x.HomeOptionsCategoryId === categoryId);
                        }
                        //this._selectedOptionsCache = masterOptions.QuoteSelectedOptions;
                    }

                }
                return masterOptions;
            })
            .catch(this._http.handleError);
    }

    deleteSelectedOptions(quoteId: number, modifiedBy: string): Observable<Response> {
		return this._http.delete(`${this.baseUrl}/api/selectedoption/${quoteId}/${modifiedBy}`)
			.map(x => x.json())
            .catch(this._http.handleError);
    }

    getPartCategories(): Observable<PartCategory[]> {
        return this._http.get(`${this.baseUrl}/api/partCategories`)
            .map(x => x.json())
            .catch(this._http.handleError);
    }

    /// Inserts/ Updates/ Deletes the passed in SelectedOptions
    saveSelectedOptions(selectedOption: SelectedOption[], categoryId: number, modelId: number, pricingView: number, adjustment: Adjustment): Observable<SelectedOption[]> {
        let jsonBody = selectedOption;
        jsonBody.forEach((op) => {
            let unitPrice = op.UnitPriceOverride !== null ? op.UnitPriceOverride : op.UnitPrice

            // 20170922 CJW - total price should always be the unit price/unit price override * quantity (no adjustments applied)
            op.TotalPrice = unitPrice * op.Quantity;
            op.AdjustedTotalPrice = Number(op.UIUnitPrice) * op.Quantity;

            if (pricingView === 1 && adjustment !== undefined) {
                if (adjustment.optionsAdjustmentPercent !== undefined && adjustment.optionsAdjustmentPercent > 0) {
                    op.UIUnitPrice = (Number(op.UIUnitPrice) * (1 + (adjustment.optionsAdjustmentPercent / 100))).toFixed(2);
                    op.AdjustedTotalPrice = Number(op.UIUnitPrice) * op.Quantity;
                }
            }

            if (op.SelectedOptionId === 0 && !op.IsCustomOption) {
                if (this._allSelectedOptionsCache !== undefined && this._allSelectedOptionsCache !== null && this._allSelectedOptionsCache.length > 0) {
                    let so = this._allSelectedOptionsCache.find(x => x.HomeOptionsOptionId === op.HomeOptionsOptionId);
                    if (so != null) {
                        op.SelectedOptionId = so.SelectedOptionId;
                    }
                }
            }
        });

        return this._http.put(`${this.baseUrl}/api/selectedoption/${modelId}`, JSON.stringify(jsonBody))
            .map(x => {
                let optionsDB: SelectedOption[] = x.json();
                if (optionsDB !== undefined && optionsDB.length > 0) {
                    optionsDB.forEach(optionDB => { optionDB.WasChanged = false; optionDB.WasUIChanged = false; })
                    this._allSelectedOptionsCache = optionsDB;
                    optionsDB = optionsDB.filter(j => j.HomeOptionsCategoryId === +categoryId);
                    //this._selectedOptionsCache = optionsDB;
                }
                return optionsDB;
            })
            .catch(this._http.handleError);
    }

    calculatePricing(normalOptions: Option[], selectedOptions: SelectedOption[], customOptions: SelectedOption[], adjustment: Adjustment, currentPricingView: number): MasterOptionsModel {
        let masterOptionsModel: MasterOptionsModel = new MasterOptionsModel();
        if (normalOptions !== undefined && normalOptions.length > 0) {
            normalOptions.forEach((no) => {
                let unitPrice = (no.UnitPriceOverride !== null) ? (no.UnitPriceOverride) : (no.UnitPrice);
                if (currentPricingView === 1 && (unitPrice > 0.00)) {
                    if (adjustment === null || adjustment === undefined) {
                        no.UIUnitPrice = unitPrice.toFixed(2);
                        no.AdjustedTotalPrice = (unitPrice * no.Quantity);
                    } else {
                        if (adjustment.optionsAdjustmentPercent !== 0 && adjustment.optionsAdjustmentPercent !== undefined) {
                            no.UIUnitPrice = (unitPrice * (1 + (adjustment.optionsAdjustmentPercent / 100))).toFixed(2).toString();
                            no.AdjustedTotalPrice = (Number(no.UIUnitPrice) * no.Quantity);

                        }
                    }
                } else {
                    no.UIUnitPrice = unitPrice.toFixed(2).toString();
                    no.AdjustedTotalPrice = (unitPrice * no.Quantity);
                }
                if (no.IsExcluded) {
                    no.AdjustedTotalPrice = 0;
                    no.TotalPrice = 0;
                }
            });
        }

        if (selectedOptions !== undefined && selectedOptions !== null && selectedOptions.length > 0) {
            selectedOptions.forEach((so) => {
                let unitPrice = (so.UnitPriceOverride !== null) ? (so.UnitPriceOverride) : (so.UnitPrice);
                if (currentPricingView === 1 && (unitPrice > 0.00)) {
                    if (adjustment === null || adjustment === undefined) {
                        so.UIUnitPrice = unitPrice.toFixed(2);
                        so.AdjustedTotalPrice = (Number(so.UIUnitPrice) * so.Quantity);
                    } else {
                        if (adjustment.optionsAdjustmentPercent !== 0 && adjustment.optionsAdjustmentPercent !== undefined) {
                            so.UIUnitPrice = (unitPrice * (1 + (adjustment.optionsAdjustmentPercent / 100))).toFixed(2).toString();
                            so.AdjustedTotalPrice = (Number(so.UIUnitPrice) * so.Quantity);
                        } else {
                            so.UIUnitPrice = unitPrice.toFixed(2).toString();
                            so.AdjustedTotalPrice = (Number(so.UIUnitPrice) * so.Quantity);
                        }
                    }
                } else {
                    so.UIUnitPrice = unitPrice.toFixed(2).toString();
                    so.AdjustedTotalPrice = (Number(so.UIUnitPrice) * so.Quantity);
                }
                if (this._allIncludeExcludeCache !== undefined && this._allIncludeExcludeCache !== null && this._allIncludeExcludeCache.length > 0) {
                    let ieo = this._allIncludeExcludeCache.filter(x => x.OptionId === so.HomeOptionsOptionId);
                    if (ieo !== undefined && ieo !== null && (ieo.length > 0)) {
                        if (ieo[0].IncludedOption && !ieo[0].IncludeOptionPrice) {
                            so.AdjustedTotalPrice = 0;
                            so.TotalPrice = 0;
                        }
                    }
                }
            });
        }

        if (customOptions !== undefined && customOptions !== null && customOptions.length > 0) {
            customOptions.forEach((so) => {
                let unitPrice = (so.UnitPriceOverride !== null) ? (so.UnitPriceOverride) : (so.UnitPrice);
                if (currentPricingView === 1 && (unitPrice > 0.00)) {
                    if (adjustment === null || adjustment === undefined) {
                        so.UIUnitPrice = unitPrice.toFixed(2);
                        so.AdjustedTotalPrice = (Number(so.UIUnitPrice) * so.Quantity);
                    } else {
                        if (adjustment.optionsAdjustmentPercent !== 0 && adjustment.optionsAdjustmentPercent !== undefined) {
                            so.UIUnitPrice = (unitPrice * (1 + (adjustment.optionsAdjustmentPercent / 100))).toFixed(2).toString();
                            so.AdjustedTotalPrice = (Number(so.UIUnitPrice) * so.Quantity);
                        } else {
                            so.UIUnitPrice = unitPrice.toFixed(2).toString();
                            so.AdjustedTotalPrice = (Number(so.UIUnitPrice) * so.Quantity);
                        }
                    }
                } else {
                    so.UIUnitPrice = unitPrice.toFixed(2).toString();
                    so.AdjustedTotalPrice = (Number(so.UIUnitPrice) * so.Quantity);
                }
            });
        }
        masterOptionsModel.NormalOptions = normalOptions;
        masterOptionsModel.QuoteSelectedOptions = selectedOptions;
        masterOptionsModel.QuoteCustomOptions = customOptions;
        return masterOptionsModel;
    }

    calculatePricingRefactor(masterOptions: MasterOptionsModel, adjustment: Adjustment, currentPricingView: number): MasterOptionsModel {
        let masterOptionsModel: MasterOptionsModel = masterOptions;

        if (masterOptions.AllSelectedOptions !== undefined && masterOptions.AllSelectedOptions !== null && masterOptions.AllSelectedOptions.length > 0) {

            masterOptions.AllSelectedOptions.forEach((so) => {
                let unitPrice = (so.UnitPriceOverride !== null) ? (so.UnitPriceOverride) : (so.UnitPrice);
                if (currentPricingView === 1 && (unitPrice > 0.00)) {
                    if (adjustment === null || adjustment === undefined) {
                        so.UIUnitPrice = unitPrice.toFixed(2);
                        so.AdjustedTotalPrice = (Number(so.UIUnitPrice) * so.Quantity);
                    } else {
                        if (adjustment.optionsAdjustmentPercent !== 0 && adjustment.optionsAdjustmentPercent !== undefined) {
                            so.UIUnitPrice = (unitPrice * (1 + (adjustment.optionsAdjustmentPercent / 100))).toFixed(2).toString();
                            so.AdjustedTotalPrice = (Number(so.UIUnitPrice) * so.Quantity);
                        } else {
                            so.UIUnitPrice = unitPrice.toFixed(2).toString();
                            so.AdjustedTotalPrice = (Number(so.UIUnitPrice) * so.Quantity);
                        }
                    }
                } else {
                    so.UIUnitPrice = unitPrice.toFixed(2).toString();
                    so.AdjustedTotalPrice = (Number(so.UIUnitPrice) * so.Quantity);
                }
                if (this._allIncludeExcludeCache !== undefined && this._allIncludeExcludeCache !== null && this._allIncludeExcludeCache.length > 0) {
                    let ieo = this._allIncludeExcludeCache.filter(x => x.OptionId === so.HomeOptionsOptionId);
                    if (ieo !== undefined && ieo !== null && (ieo.length > 0)) {
                        if (ieo[0].IncludedOption && !ieo[0].IncludeOptionPrice) {
                            so.AdjustedTotalPrice = 0;
                            so.TotalPrice = 0;
                        }
                    }
                }
            });
        }

        return masterOptionsModel;
    }

    /// Converts all the SelectedOptions into a single list for saving purposes
    setMappedSelectedOptions(selectedOptions: Array<SelectedOption>, customOptions: Array<SelectedOption>, modifiedBy: string, quoteId: number) {
        if (selectedOptions && modifiedBy && quoteId) {
            let mappedOptions = new Array<SelectedOption>();
            let enableSaveButton: boolean = true;

			selectedOptions.filter(so => so.WasChanged === true || so.WasUIChanged === true).forEach(soItem => {
				soItem.QuoteRevisionNumber = this._quote !== undefined && this._quote !== null ? this._quote.revisionNumber : 1;
				mappedOptions.push(soItem);
			});

            /// Saves the CustomOptions into a temp list
            if (customOptions !== undefined && customOptions !== null && customOptions.length > 0) {
                customOptions.forEach((option) => {
                    if (option.WasChanged === true || option.WasUIChanged === true) {
                        option.ModifiedBy = modifiedBy;
						option.TotalPrice = option.UnitPrice * option.Quantity;
						option.QuoteRevisionNumber = this._quote !== undefined && this._quote !== null ? this._quote.revisionNumber : 1;
                        mappedOptions.push(option);
                    }
                    if (enableSaveButton) {
                        enableSaveButton = option.IsValidUnitPrice;
                    }
                });
            }
            this.sendOptionsToService(mappedOptions);
        }
    }

    sendOptionsToService(mappedOptions: Array<any>) {
        this.mappedOptions$.next(mappedOptions);
    }

    /// This was called in multiple components so I moved it here in a central area
    setTrailingZero(valueString: string): string {
        return (valueString !== undefined && valueString !== null ? Number(valueString).toFixed(2) : '0.00');
    }

}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/@angularclass/hmr-loader!./src/app/services/options.service.ts